import 'reset-css';
import 'swiper/css';
import './scss/index.scss';
import React, {useEffect} from 'react';
import {Provider} from 'react-redux';
import {persistor, store} from './store/index';
import {PersistGate} from 'redux-persist/integration/react';

import {StackNavigator} from './navigation/StackNavigator';
import { requestNotificationPermission } from './services/firebase';
import { initCapacitor, isNative, getCapacitorFcmToken } from './services/capacitorService';
import { startNetworkListener } from './services/networkService';
import { offlineQueue } from './services/offlineQueueService';
import { setOnlineStatus, setPendingCount, setFailedCount } from './store/slices/offlineSlice';
import { runSync } from './services/syncService';
import { hooks } from './hooks';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { UiPreferencesProvider } from './context/UiPreferencesContext';
import { OfflineBanner } from './components/OfflineBanner';
import { IncomingCallOverlay } from './components/IncomingCallOverlay';

const AppContent = () => {
  const { t } = useLanguage();
  const authSlice = hooks.useAppSelector((state) => state.authSlice);
  const userIdRef = React.useRef(authSlice.userId);
  React.useEffect(() => { userIdRef.current = authSlice.userId; }, [authSlice.userId]);

  const dispatch = hooks.useAppDispatch();

  // Init Capacitor sekali saat mount (back button, splash screen, push notif)
  useEffect(() => {
    initCapacitor();
  }, []);

  // Network listener + pending count
  useEffect(() => {
    const refreshPendingCount = async () => {
      const [count, failed] = await Promise.all([
        offlineQueue.getPendingCount(),
        offlineQueue.getPermanentlyFailedCount(),
      ]);
      dispatch(setPendingCount(count));
      dispatch(setFailedCount(failed));
    };

    startNetworkListener(async (isOnline) => {
      dispatch(setOnlineStatus(isOnline));
      if (isOnline) {
        // Jalankan sync saat koneksi pulih, sertakan userId untuk deteksi mismatch
        await runSync(refreshPendingCount, userIdRef.current);
      }
      await refreshPendingCount();
    });

    refreshPendingCount();
  }, [dispatch]);

  useEffect(() => {
    // Fungsi reusable untuk mengirim token ke backend
    const sendTokenToBackend = async (token: string) => {
      try {
        const tokenData = { fcm_token: token };
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://apiteratasks.teralintas.com/api';

        console.log("Sending FCM Token to:", `${apiBaseUrl}/users/fcm-token`);
        const response = await fetch(`${apiBaseUrl}/users/fcm-token`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authSlice.accessToken}`
          },
          body: JSON.stringify(tokenData)
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        console.log("FCM Token sent to backend successfully");
      } catch (error) {
        console.error("Failed to send FCM token to backend:", error);
      }
    };

    // Bridge: dipanggil oleh Flutter WebView, Capacitor push-notifications, atau keduanya
    (window as any).setFcmToken = (token: string) => {
      console.log("Received FCM Token:", token);
      if (authSlice.isAuthenticated && authSlice.accessToken) {
        sendTokenToBackend(token);
      }
    };

    if (authSlice.isAuthenticated && authSlice.accessToken) {
      if (isNative) {
        // Capacitor: jika token sudah terdaftar sebelum user login, kirim sekarang
        const token = getCapacitorFcmToken();
        if (token) sendTokenToBackend(token);
      } else {
        // Web browser: gunakan Firebase JS SDK
        requestNotificationPermission().then(token => {
          if (token) sendTokenToBackend(token);
        });
      }
    }
  }, [authSlice.isAuthenticated, authSlice.accessToken]);

  // Version check: deteksi deploy baru, terutama untuk Huawei yang sering cache index.html
  const initialVersion = React.useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = React.useState(false);
  const [isUpdating, setIsUpdating]           = React.useState(false);
  const [updateProgress, setUpdateProgress]   = React.useState(0);
  const updateDelayRef = React.useRef(0);

  const handleUpdate = () => {
    const delay = Math.floor(Math.random() * 4000) + 1000;
    updateDelayRef.current = delay;
    setIsUpdating(true);
    setUpdateProgress(0);
    // Double rAF ensures the 0% width renders before the transition starts
    requestAnimationFrame(() => requestAnimationFrame(() => setUpdateProgress(88)));
    setTimeout(() => {
      setUpdateProgress(100);
      setTimeout(() => window.location.reload(), 250);
    }, delay);
  };

  React.useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const v = data?.v;
        if (!v) return;
        if (initialVersion.current === null) {
          initialVersion.current = v;
        } else if (initialVersion.current !== v) {
          setUpdateAvailable(true);
        }
      } catch { /* ignore network errors */ }
    };

    checkVersion();

    // Cek saat app kembali ke foreground
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  return (
    <>
      <OfflineBanner />
      <IncomingCallOverlay
        isAuthenticated={!!authSlice.isAuthenticated}
        userName={authSlice.userName || authSlice.email || 'User'}
        accessToken={authSlice.accessToken}
      />
      {updateAvailable && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
          backgroundColor: '#1D4ED8', color: '#fff',
          padding: '10px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 12, fontSize: 13,
        }}>
          <span>{t.common.updateAvailable}</span>
          <button
            onClick={handleUpdate}
            style={{
              backgroundColor: '#fff', color: '#1D4ED8', border: 'none',
              borderRadius: 6, padding: '4px 12px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {t.common.updateNow}
          </button>
        </div>
      )}
      {isUpdating && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999999,
          backgroundColor: '#0F172A',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 20,
        }}>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 500 }}>
            {t.common.updating}
          </div>
          <div style={{ width: 260, height: 6, backgroundColor: '#1E3A5F', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              backgroundColor: '#3B82F6',
              borderRadius: 3,
              width: `${updateProgress}%`,
              transition: updateProgress === 0
                ? 'none'
                : updateProgress >= 100
                  ? 'width 0.25s ease'
                  : `width ${updateDelayRef.current}ms ease-out`,
            }} />
          </div>
        </div>
      )}
      <StackNavigator />
    </>
  );
};

function App() {
  return (
    <div id='app'>
      <Provider store={store}>
        <PersistGate
          loading={null}
          persistor={persistor}
        >
          <LanguageProvider>
            <UiPreferencesProvider>
              {/* <StackNavigator /> */}
              <AppContent />
            </UiPreferencesProvider>
          </LanguageProvider>
        </PersistGate>
      </Provider>
    </div>
  );
}

export default App;
